import { Point } from 'highcharts';
import {
    AajeevikaActivityType,
    CardsData,
    DARData,
    DashboardMetric,
    EnterpriseCard,
    IncomeDistributionData,
    LIVELIHOOD_SELECTOR,
} from '../../../_models/lakhpati-didi-dashboard.model';
import { CroreFormatPipe } from '../../shared/_pipes/crore-format.pipe';
import { IndianFormatPipe } from '../../shared/_pipes/indian-format.pipe';

export const CARD_DATA = (
    cardDataArr: number[],
    progress: number[]
): DashboardMetric[] => {
    return [
        {
            title: 'Target PLDs',
            value: cardDataArr[0],
            description: 'Overall Goal Assigned',
            icon: 'assets/images/card-1.svg',
            iconColor: '#F973161A',
        },
        {
            title: 'Identified PLDs',
            value: cardDataArr[1],
            description: 'Vs Target',
            icon: 'assets/images/card-2.svg',
            progress: progress[0],
            progressBarColor: '#B043FF',
            iconColor: '#F7ECFF',
        },
        {
            title: 'Approved PLDs',
            value: cardDataArr[2],
            description: 'Vs Identified',
            icon: 'assets/images/card-3.svg',
            progress: progress[1],
            progressBarColor: '#10B981',
            iconColor: '#10B9811A',
        },
        {
            title: 'Active PLDs',
            value: cardDataArr[3],
            description: 'Vs Approved',
            icon: 'assets/images/card-4.svg',
            progress: progress[2],
            progressBarColor: '#00949A',
            iconColor: '#00BCD41A',
        },
        {
            title: 'Verified PLDs',
            value: cardDataArr[4],
            description: 'Vs Approved',
            icon: 'assets/images/card-5.svg',
            progress: progress[3],
            progressBarColor: '#D0459C',
            iconColor: '#FFE5F5',
        },
        {
            title: 'Remaining PLDs',
            value: cardDataArr[5],
            description: 'Vs Approved',
            icon: 'assets/images/card-6.svg',
            progress: progress[4],
            progressBarColor: '#9CA3AF',
            iconColor: '#F0F0F0',
        },
        {
            title: 'CRPs Onboarded',
            value: cardDataArr[6],
            description: 'Overall CRPs Onboarded',
            icon: 'assets/images/card-7.svg',
            iconColor: '#FFF5E2',
        },
        {
            title: 'PLDs Per CRP',
            value: cardDataArr[7],
            description: 'PLDs vs CRPs Ratio',
            icon: 'assets/images/card-8.svg',
            iconColor: '#E8ECFF',
        },
        {
            title: 'Total Entries (DARs)',
            value: cardDataArr[8],
            description: 'Overall DAR Entries',
            icon: 'assets/images/card-9.svg',
            iconColor: '#F973161A',
        },
    ];
};

export const ENTERPRISE_CARDS = (cardData: number[]): EnterpriseCard[] => {
    return [
        {
            title: 'PLDs in Farm Enterprises',
            value: cardData[0],
            icon: 'assets/images/card-10.svg',
            backgroundColor: '#10B9811A',
        },
        {
            title: 'PLDs in Non-Farm Enterprises',
            value: cardData[1],
            icon: 'assets/images/card-11.svg',
            backgroundColor: '#F973161A',
        },
        {
            title: 'PLDs in Other Enterprises',
            value: cardData[2],
            icon: 'assets/images/card-12.svg',
            backgroundColor: '#3250FF1A',
        },
    ];
};

export const DAR_OPTIONS = (
    indianFormatPipe: IndianFormatPipe
): Highcharts.Options => {
    const colors = ['#45BBFC', '#FFC107', '#FF7C35', '#20C997'];
    return {
        title: {
            text: undefined,
        },
        chart: {
            type: 'column',
            inverted: false,
            backgroundColor: 'transparent',
        },
        credits: { enabled: false },
        xAxis: {
            categories: ['Q1', 'Q2', 'Q3', 'Q4'],
            lineWidth: 0,
            labels: {
                useHTML: true,
                style: { fontSize: '14px', color: '#667085' },
                formatter() {
                    return `
                        <span style="display:flex;align-items:center;gap:6px;">
                        <span style="
                            width:10px;
                            height:10px;
                            border-radius:50%;
                            background:${colors[this.pos]};
                            display:inline-block;">
                        </span>
                        ${this.value}
                        </span>
                    `;
                },
            },
        },
        yAxis: {
            title: {
                text: 'Unique Entries',
                style: {
                    textAlign: 'center',
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: '400',
                },
            },
            labels: {
                formatter: function () {
                    const value = this.value as number;
                    if (value >= 10000000) {
                        return value / 10000000 + ' Cr'; // Crore
                    } else if (value >= 100000) {
                        return value / 100000 + ' Lakh'; // Lakh
                    } else {
                        return value.toString();
                    }
                },
            },
            type: 'logarithmic',
        },
        tooltip: {
            hideDelay: 0,
            animation: false,
            useHTML: true,
            backgroundColor: '#2c2f4b',
            borderRadius: 8,
            borderWidth: 0,
            style: { color: '#fff', fontSize: '14px' },
            formatter() {
                return `
                        <div style="display:flex;align-items:center;gap:8px;">
                            <span style="
                            width:10px;
                            height:10px;
                            border-radius:50%;
                            background:${this.color};
                            display:inline-block;">
                            </span>
                            <span><b>${indianFormatPipe.transform(this.y!)}</b></span>
                        </div>
                    `;
            },
        },
        plotOptions: {
            column: {
                borderRadius: 6,
                borderWidth: 0,
            },
        },
        legend: { enabled: false },
        series: [
            {
                type: 'column',
                name: 'Unique Entries',
                colorByPoint: true,
                colors,
                data: [0, 0, 0, 0],
            },
        ],
    };
};

export const LIVELIHOOD_OPTIONS = (
    crorePipe: CroreFormatPipe,
    indianFormatPipe: IndianFormatPipe
): Highcharts.Options => {
    return {
        chart: {
            type: 'bar',
            backgroundColor: 'transparent',
        },
        title: { text: undefined },
        credits: { enabled: false },
        yAxis: {
            title: { text: undefined },
            type: 'logarithmic',
            min: 1,
            tickAmount: 6,
            gridLineWidth: 0,
            labels: {
                formatter() {
                    const v = this.value as number;

                    return `${crorePipe.transform(v, true)}`;
                },
                style: {
                    color: '#667085',
                    fontSize: '13px',
                    fontFamily: 'Inter',
                },
            },
        },
        xAxis: {
            title: { text: null },
            gridLineWidth: 1,
            lineWidth: 0,
            categories: [
                'Less than 1 Lakh',
                '1-2 Lakh',
                '2-3 Lakh',
                '3-4 Lakh',
                '4-5 Lakh',
                '5-10 Lakh',
                '10-15 Lakh',
                '15- 20 Lakh',
                '20Lakh above',
            ],
            labels: {
                style: {
                    color: '#667085',
                    fontSize: '14px',
                    fontFamily: 'Inter',
                },
            },
        },
        legend: { enabled: false },
        tooltip: {
            hideDelay: 0,
            animation: false,
            backgroundColor: '#2c2f4b',
            borderWidth: 0,
            borderRadius: 8,
            useHTML: true,
            style: { color: '#fff' },
            formatter() {
                const amount = this.y!;
                return `<b>${indianFormatPipe.transform(amount)}</b>`;
            },
        },
        plotOptions: {
            bar: {
                pointWidth: 26,
                borderRadius: 8,
                pointPadding: 0.2,
                groupPadding: 0.1,
            },
        },
        series: [
            {
                type: 'bar',
                name: 'Income',
                color: '#2E90FA',
                data: [0, 0, 0, 0, 0, 0],
            },
        ],
    };
};

export const FY_LIST = [
    {
        label: 'FY 2024-25',
        data: {
            fromYear: 2024,
            toYear: 2025,
        },
    },
    {
        label: 'FY 2025-26',
        data: {
            fromYear: 2025,
            toYear: 2026,
        },
    },
];

export const QUARTER_LIST = ['Q1', 'Q2', 'Q3', 'Q4'];

export const LIVELIHOOD_SELECTION: {
    label: LIVELIHOOD_SELECTOR;
    data: string;
}[] = [
    { label: 'Select Enterprise', data: 'ENTERPRISE' },
    { label: 'Farm', data: 'FARM' },
    { label: 'Non-Farm', data: 'NON_FARM' },
    { label: 'Others', data: 'OTHER' },
];

export const DAR_SELCTION_DATA = [
    {
        label: 'Entered',
        data: 'entered',
    },
    {
        label: 'Rejected',
        data: 'rejected',
    },
    {
        label: 'Pending',
        data: 'pending',
    },
    {
        label: 'Approved',
        data: 'approved',
    },
];

export const colors: Record<string, string> = {
    crop: '#AC7CFF',
    auqa: '#72F1FA',
    animal: '#4FEAD0',
    ntfp: '#FFD35E',
    manufacturing: '#51B5FD',
    trading: '#C3F38C',
    service: '#FFAD6B',
    salary: '#FD738A',
    wages: '#FFD259',
};

export const CHART_COLOR = (backgroundColor: string[]): string[] => {
    return backgroundColor.map((color) => colors[color]);
};

export const PIE_CHART_OPTIONS = (
    indianFormatPipe: IndianFormatPipe
): Highcharts.Options => {
    return {
        credits: { enabled: false },
        title: { text: '' },
        tooltip: {
            hideDelay: 0,
            animation: false,
            useHTML: true,
            backgroundColor: 'transparent',
            borderWidth: 0,
            shadow: false,
            style: { pointerEvents: 'none' },
            formatter() {
                const color = this.color || '#666';
                const formatted = indianFormatPipe.transform(
                    (this as Point & { originalValue: number }).originalValue
                );
                return `<div style="
                                background: #20304F;
                                color: #fff;
                                padding: 12px 16px;
                                border-radius: 10px;
                                font-weight:600;
                                display: inline-flex;
                                align-items: center;
                                gap: 8px;
                            ">
                                <span style="width: 10px; height: 10px; border-radius: 50%; background:${color}; display: inline-block"></span>
                                <span>${formatted}</span>
                            </div>`;
            },
        },
        legend: {
            enabled: false,
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom',
            symbolRadius: 6,
            itemStyle: { fontSize: '14px', fontWeight: '600' },
        },
    };
};

export const cumulateDARData = (
    q1Data: DARData[],
    q2Data: DARData[],
    q3Data: DARData[],
    q4Data: DARData[]
) => {
    let cumEnteredQ1 = 0,
        cumApprovedQ1 = 0,
        cumPendingQ1 = 0,
        cumRejectedQ1 = 0,
        cumEnteredQ2 = 0,
        cumApprovedQ2 = 0,
        cumPendingQ2 = 0,
        cumRejectedQ2 = 0,
        cumEnteredQ3 = 0,
        cumApprovedQ3 = 0,
        cumPendingQ3 = 0,
        cumRejectedQ3 = 0,
        cumEnteredQ4 = 0,
        cumApprovedQ4 = 0,
        cumPendingQ4 = 0,
        cumRejectedQ4 = 0;

    q1Data.forEach((entry) => {
        cumEnteredQ1 += entry.pldFilledRegister ?? 0;
        cumApprovedQ1 += entry.pldFilledRegisterApproved ?? 0;
        cumPendingQ1 += entry.pldFilledRegisterPending ?? 0;
        cumRejectedQ1 += entry.pldFilledRegisterRejected ?? 0;
    });

    q2Data.forEach((entry) => {
        cumEnteredQ2 += entry.pldFilledRegister ?? 0;
        cumApprovedQ2 += entry.pldFilledRegisterApproved ?? 0;
        cumPendingQ2 += entry.pldFilledRegisterPending ?? 0;
        cumRejectedQ2 += entry.pldFilledRegisterRejected ?? 0;
    });

    q3Data.forEach((entry) => {
        cumEnteredQ3 += entry.pldFilledRegister ?? 0;
        cumApprovedQ3 += entry.pldFilledRegisterApproved ?? 0;
        cumPendingQ3 += entry.pldFilledRegisterPending ?? 0;
        cumRejectedQ3 += entry.pldFilledRegisterRejected ?? 0;
    });

    q4Data.forEach((entry) => {
        cumEnteredQ4 += entry.pldFilledRegister ?? 0;
        cumApprovedQ4 += entry.pldFilledRegisterApproved ?? 0;
        cumPendingQ4 += entry.pldFilledRegisterPending ?? 0;
        cumRejectedQ4 += entry.pldFilledRegisterRejected ?? 0;
    });

    const data = {
        entered: [cumEnteredQ1, cumEnteredQ2, cumEnteredQ3, cumEnteredQ4],
        approved: [cumApprovedQ1, cumApprovedQ2, cumApprovedQ3, cumApprovedQ4],
        pending: [cumPendingQ1, cumPendingQ2, cumPendingQ3, cumPendingQ4],
        rejected: [cumRejectedQ1, cumRejectedQ2, cumRejectedQ3, cumRejectedQ4],
    };
    return data;
};

export const cumulateFarmData = (data: IncomeDistributionData[]) => {
    let cumAH = 0;
    let cumAF = 0;
    let cumCB = 0;
    let cumNTFP = 0;
    data.forEach((entry) => {
        cumAH += entry.totalNoOfDetailsExercisingAnimalHusbandryActivities ?? 0;
        cumAF += entry.totalNoOfDetailsExercisingAquaFarmingActivities ?? 0;
        cumCB += entry.totalNoOfDetailsExercisingCropBasedActivities ?? 0;
        cumNTFP += entry.totalNoOfDetailsExercisingNftpActivities ?? 0;
    });
    const newData = [
        { name: 'Crop Based', y: cumCB },
        { name: 'Animal Husbandry', y: cumAH },
        { name: 'NTFP', y: cumNTFP },
        { name: 'Aqua Farming', y: cumAF },
    ];
    return newData;
};

export const cumulateNonFarmData = (data: IncomeDistributionData[]) => {
    let cumManufacturing = 0;
    let cumTrading = 0;
    let cumServices = 0;
    data.forEach((entry) => {
        cumManufacturing +=
            entry.totalNoOfDetailsExercisingManufacturingActivities ?? 0;
        cumTrading += entry.totalNoOfDetailsExercisingTradingActivities ?? 0;
        cumServices += entry.totalNoOfDetailsExercisingServiceActivities ?? 0;
    });
    const newData = [
        { name: 'Manufacturing', y: cumManufacturing },
        { name: 'Trading', y: cumTrading },
        { name: 'Services', y: cumServices },
    ];
    return newData;
};

export const cumulateOthersData = (data: IncomeDistributionData[]) => {
    let cumWages = 0;
    let cumRegularSource = 0;
    data.forEach((entry) => {
        cumWages += entry.totalNoOfDetailsExercisingWageBasedActivities ?? 0;
        cumRegularSource +=
            entry.totalNoOfDetailsExercisingSalaryBasedpActivities ?? 0;
    });
    const newData = [
        { name: 'Salary', y: cumWages },
        { name: 'Wages', y: cumRegularSource },
    ];
    return newData;
};

export const cumulateCount = (res: IncomeDistributionData[]) => {
    let lessThan1LakhTotal = 0;
    let oneTo2LakhTotal = 0;
    let twoTo3LakhTotal = 0;
    let threeTo4LakhTotal = 0;
    let fourTo5LakhTotal = 0;
    let fiveTo10LakhTotal = 0;
    let tenTo15LakhTotal = 0;
    let fifteenTo20LakhTotal = 0;
    let above20LakhTotal = 0;

    let sum_totalNoOfDetailsInAnnualIncomeSlab_0_25000 = 0;
    let sum_totalNoOfDetailsInAnnualIncomeSlab_25001_60000 = 0;
    let sum_totalNoOfDetailsInAnnualIncomeSlab_60001_99999 = 0;

    res.forEach((data) => {
        sum_totalNoOfDetailsInAnnualIncomeSlab_0_25000 +=
            data.totalNoOfDetailsInAnnualIncomeSlab_0_25000;
        sum_totalNoOfDetailsInAnnualIncomeSlab_25001_60000 +=
            data.totalNoOfDetailsInAnnualIncomeSlab_25001_60000;
        sum_totalNoOfDetailsInAnnualIncomeSlab_60001_99999 +=
            data.totalNoOfDetailsInAnnualIncomeSlab_60001_99999;

        oneTo2LakhTotal +=
            data.totalNoOfDetailsInAnnualIncomeSlab_100000_200000;
        twoTo3LakhTotal +=
            data.totalNoOfDetailsInAnnualIncomeSlab_200001_300000;
        threeTo4LakhTotal +=
            data.totalNoOfDetailsInAnnualIncomeSlab_300001_400000;
        fourTo5LakhTotal +=
            data.totalNoOfDetailsInAnnualIncomeSlab_400001_500000;
        fiveTo10LakhTotal +=
            data.totalNoOfDetailsInAnnualIncomeSlab_500001_1000000;
        tenTo15LakhTotal +=
            data.totalNoOfDetailsInAnnualIncomeSlab_1000001_1500000;
        fifteenTo20LakhTotal +=
            data.totalNoOfDetailsInAnnualIncomeSlab_1500001_2000000;
        above20LakhTotal +=
            data.totalNoOfDetailsInAnnualIncomeSlab_2000001_Above;
    });

    lessThan1LakhTotal =
        sum_totalNoOfDetailsInAnnualIncomeSlab_0_25000 +
        sum_totalNoOfDetailsInAnnualIncomeSlab_25001_60000 +
        sum_totalNoOfDetailsInAnnualIncomeSlab_60001_99999;

    const newData = [
        lessThan1LakhTotal,
        oneTo2LakhTotal,
        twoTo3LakhTotal,
        threeTo4LakhTotal,
        fourTo5LakhTotal,
        fiveTo10LakhTotal,
        tenTo15LakhTotal,
        fifteenTo20LakhTotal,
        above20LakhTotal,
    ];
    return newData;
};

export const cumulateCardData = (data: CardsData[]) => {
    return data.reduce(
        (acc, curr) => {
            acc.cumTarget += curr.targetPldFy2425 || 0;
            acc.cumIdentified += curr.identifiedpldCount || 0;
            acc.cumApproved += curr.approvedpldCount || 0;
            acc.cumActive += curr.approvedactivepldCount || 0;
            acc.cumVerified += curr.verifiedCount || 0;
            acc.cumOnboarded += curr.crponboardedCount || 0;
            acc.cumTotalEntries += curr.quartersEntered || 0;
            return acc;
        },
        {
            cumTarget: 0,
            cumIdentified: 0,
            cumApproved: 0,
            cumActive: 0,
            cumVerified: 0,
            cumOnboarded: 0,
            cumTotalEntries: 0,
        }
    );
};

export const cumulateEnterpriseCards = (
    farmCardData: IncomeDistributionData[],
    nonFarmCardData: IncomeDistributionData[],
    othersCardData: IncomeDistributionData[]
) => {
    const farmCardTotal = farmCardData.reduce(
        (sum, item) => sum + item.totalNoOfDetailsCaptured,
        0
    );
    const nonFarmCardTotal = nonFarmCardData.reduce(
        (sum, item) => sum + item.totalNoOfDetailsCaptured,
        0
    );
    const othersCardTotal = othersCardData.reduce(
        (sum, item) => sum + item.totalNoOfDetailsCaptured,
        0
    );

    return [farmCardTotal, nonFarmCardTotal, othersCardTotal];
};

export const cumulateUniqueDarData = (data: IncomeDistributionData[]) => {
    let totalUniqueDar = 0;
    data.forEach((entry) => {
        totalUniqueDar += entry.totalNoOfDetailsCaptured ?? 0;
    });
    return totalUniqueDar;
};

export const getCategoryIdByLabel = (
    arr: AajeevikaActivityType[],
    labelName: string
) => arr.find((item) => item.label === labelName)?.value;
